import React, { useState, useEffect, useCallback } from "react";
import { GoogleMap, LoadScriptNext, InfoWindow, Marker } from "@react-google-maps/api";
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from "react-places-autocomplete";
import PageHeader from "./PageHeader";
import { useTheme } from "./ThemeContext";
import { useAuth } from "./AuthContext";
import Spinner from "./Spinner";
import MobileHeader from './MobileHeader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faList, faArrowDown, faArrowUp, faTimes, faMapMarkerAlt, faDownload, faFileCsv, faFilePdf, faTable, faMap, faSort, faSortUp, faSortDown, faSearch, faClipboardList } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import jsPDF from 'jspdf';
import { saveAs } from 'file-saver';

const googleMapsLibraries = ["places"];

const AdminLocationFinder = () => {
  const { currentUser } = useAuth();
  const [address, setAddress] = useState("");
  const [coordinates, setCoordinates] = useState({});
  const [radius, setRadius] = useState("");
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [discountGroups, setDiscountGroups] = useState([]);
  const [selectedDiscountGroup, setSelectedDiscountGroup] = useState("");
  const [selectedDiscountGroupDetails, setSelectedDiscountGroupDetails] = useState(null);
  const [carrierName, setCarrierName] = useState("");
  const [notes, setNotes] = useState("");
  const { theme } = useTheme();
  const [isFormValid, setIsFormValid] = useState(false);
  const [viewMode, setViewMode] = useState('table'); // 'table', 'map', 'list'
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [hasSearched, setHasSearched] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [mapInstance, setMapInstance] = useState(null);
  const [mapCenter, setMapCenter] = useState({ lat: 39.8283, lng: -98.5795 });
  const [mapZoom, setMapZoom] = useState(4);
  const apiUrl = process.env.REACT_APP_API_URL;
  const ipInfoToken = 'ad22ffe4eb7ee0';

  // Check admin permissions
  useEffect(() => {
    if (!currentUser?.isAdmin) {
      window.location.href = '/';
    }
  }, [currentUser]);

  const defaultMapStyles = [
    {
      featureType: "all",
      elementType: "labels.text.fill",
      stylers: [{ color: '#000000' }],
    },
    {
      featureType: "poi",
      elementType: "labels.icon",
      stylers: [{ color: theme.primary }],
    },
    {
      "featureType": "water",
      "elementType": "geometry",
      "stylers": [
        { "color": "#b0e0e6" },
        { "lightness": 40 },
        { "saturation": -30 }
      ]
    },
  ];

  useEffect(() => {
    const fetchUserLocation = async () => {
      try {
        const response = await axios.get(`https://ipinfo.io/json?token=${ipInfoToken}`);
        const [lat, lng] = response.data.loc.split(',');
        setUserLocation({ lat: parseFloat(lat), lng: parseFloat(lng) });
      } catch (error) {
        console.error("Error getting user location from IPInfo:", error);
      }
    };

    fetchUserLocation();
  }, []);

  useEffect(() => {
    setCoordinates({});
  }, [address]);

  useEffect(() => {
    const isValid = address.trim() !== "" && radius > 0 && radius <= 3500 && selectedDiscountGroup !== "" && carrierName.trim() !== "";
    setIsFormValid(isValid);
  }, [address, radius, selectedDiscountGroup, carrierName]);

  // Fetch discount groups for admin
  useEffect(() => {
    const fetchDiscountGroups = async () => {
      try {
        const response = await fetch(`${apiUrl}/dataServer`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            action: 'getDiscountGroups',
            payload: { userId: currentUser.userId }
          })
        });

        if (response.ok) {
          const fetchedData = await response.json();
          console.log("Fetched Discount Groups:", fetchedData);
          
          if (Array.isArray(fetchedData)) {
            setDiscountGroups(fetchedData);
          } else if (fetchedData?.discountGroups) {
            setDiscountGroups(fetchedData.discountGroups);
          } else {
            setDiscountGroups([]);
          }
        } else {
          console.error('Error fetching discount groups');
          setDiscountGroups([]);
        }
      } catch (error) {
        console.error('Error fetching discount groups:', error);
        setDiscountGroups([]);
      }
    };

    if (currentUser?.userId) {
      fetchDiscountGroups();
    }
  }, [currentUser, apiUrl]);

  const handleSelect = async (value) => {
    const results = await geocodeByAddress(value);
    const latLng = await getLatLng(results[0]);
    setAddress(value);
    setCoordinates(latLng);
  };

  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const toRad = (x) => (x * Math.PI) / 180;
    const R = 3958.8; // Radius of the Earth in miles

    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance in miles
  };

  const fetchFuelStops = async () => {
    if (radius > 3500) {
      alert("Radius should be 3500 or below.");
      return;
    }

    setLoading(true);
    setHasSearched(true);
    let currentCoordinates = coordinates;

    if (!isValidCoordinates && address) {
      try {
        const results = await geocodeByAddress(address);
        const latLng = await getLatLng(results[0]);
        setCoordinates(latLng);
        currentCoordinates = latLng;
        setMapCenter(latLng);
        setMapZoom(10);        
      } catch (error) {
        console.error("Error geocoding address:", error);
        setLoading(false);
        return;
      }
    }

    const data = {
      latitude: currentCoordinates.lat,
      longitude: currentCoordinates.lng,
      searchRange: radius,
      UserId: currentUser.userId,
      discountGroupId: parseInt(selectedDiscountGroup)
    };

    try {
      const response = await fetch(`${apiUrl}/getLocationsByDiscountGroup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        console.error("Fetch failed with status:", response.status, response.statusText);
        setLoading(false);
        return;
      }

      const responseData = await response.json();
      const locationsData = responseData.locations || [];
      const discountGroupDetails = responseData.discountGroup || null;

      // Calculate distance from the search city coordinates, not user location
      locationsData.forEach((location) => {
        location.distance = calculateDistance(
          currentCoordinates.lat,
          currentCoordinates.lng,
          parseFloat(location.latitude),
          parseFloat(location.longitude)
        ).toFixed(2);
      });
      
      setLocations(locationsData);
      setSelectedDiscountGroupDetails(discountGroupDetails);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching fuel stops:", error);
      setLoading(false);
    }
  };

  const isValidCoordinates =
    coordinates &&
    typeof coordinates.lat === "number" &&
    typeof coordinates.lng === "number";

  // Sorting function
  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedLocations = React.useMemo(() => {
    let sortableItems = [...locations];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aValue, bValue;
        
        switch (sortConfig.key) {
          case 'userPrice':
            aValue = parseFloat(a.userPrice) || 0;
            bValue = parseFloat(b.userPrice) || 0;
            break;
          case 'savings':
            aValue = parseFloat(a.savings) || 0;
            bValue = parseFloat(b.savings) || 0;
            break;
          case 'retailPrice':
            aValue = parseFloat(a.retailPrice) || 0;
            bValue = parseFloat(b.retailPrice) || 0;
            break;
          case 'distance':
            aValue = parseFloat(a.distance) || 0;
            bValue = parseFloat(b.distance) || 0;
            break;
          case 'locationName':
            aValue = a.locationName || '';
            bValue = b.locationName || '';
            break;
          case 'city':
            aValue = a.city || '';
            bValue = b.city || '';
            break;
          default:
            aValue = a[sortConfig.key] || '';
            bValue = b[sortConfig.key] || '';
        }

        if (typeof aValue === 'string') {
          return sortConfig.direction === 'ascending' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else {
          return sortConfig.direction === 'ascending' 
            ? aValue - bValue 
            : bValue - aValue;
        }
      });
    }
    return sortableItems;
  }, [locations, sortConfig]);

  const getSortIcon = (columnName) => {
    if (sortConfig.key !== columnName) {
      return faSort;
    }
    return sortConfig.direction === 'ascending' ? faSortUp : faSortDown;
  };

  const getChainName = (chainId) => {
    const chainNames = {
      1: 'Pilot/Flying J',
      2: "Love's",
      3: 'TA/Petro',
      4: 'Sapp Brothers',
      5: 'Wilco',
      6: 'SpeedwaySuperAmerica',
      7: 'QuickTrip',
      8: 'KangarooPantry',
      9: 'PetroCanada',
      10: 'ImperialOilEsso',
      11: 'KwikTrip',
      12: 'QuickFuel',
      13: 'Ambest',
      14: 'CircleK',
    };
    return chainNames[chainId] || 'Independent';
  };

  const exportToCSV = () => {
    if (sortedLocations.length === 0) {
      alert('No data to export');
      return;
    }

    // Summary information
    const summaryLines = [
      `Pricing Quote for ${carrierName}`,
      `Generated: ${new Date().toLocaleDateString()}`,
      `Search Location: ${address}`,
      `Search Radius: ${radius} miles`,
      `Discount Group: ${selectedDiscountGroupDetails?.name || 'N/A'}`,
      notes.trim() !== '' ? `Notes: ${notes}` : '',
      ''
    ];

    // Transaction fees and fuel types section
    if (selectedDiscountGroupDetails) {
      summaryLines.push(
        'Transaction Fees:',
        `In-Network Transaction Fee: $${parseFloat(selectedDiscountGroupDetails.transactionFee).toFixed(2)}`,
        `Out of Network Transaction Fee: $${parseFloat(selectedDiscountGroupDetails.outOfNetworkTransactionFee).toFixed(2)}`,
        `Cash Advance Fee: $${parseFloat(selectedDiscountGroupDetails.cashAdvanceFee).toFixed(2)}`,
        ''
      );

      // Add fuel types
      const fuelTypes = [];
      if (selectedDiscountGroupDetails.fuelTypes?.diesel) fuelTypes.push('Diesel');
      if (selectedDiscountGroupDetails.fuelTypes?.gas) fuelTypes.push('Gas');
      if (selectedDiscountGroupDetails.fuelTypes?.def) fuelTypes.push('DEF');
      if (selectedDiscountGroupDetails.fuelTypes?.reeferFuel) fuelTypes.push('Reefer Fuel');
      if (selectedDiscountGroupDetails.fuelTypes?.lng) fuelTypes.push('LNG');
      
      if (fuelTypes.length > 0) {
        summaryLines.push(
          `Fuel Types: ${fuelTypes.join(', ')}`,
          ''
        );
      }
    }

    const headers = [
      'Location Name',
      'Address',
      'City',
      'State',
      'ZIP',
      'Chain',
      'Retail Price',
      'Your Price',
      'Savings',
      'Distance (miles)'
    ];

    const csvContent = [
      ...summaryLines,
      headers.join(','),
      ...sortedLocations.map(location => [
        `"${location.locationName}"`,
        `"${location.addr1}"`,
        `"${location.city}"`,
        `"${location.state}"`,
        `"${location.zip}"`,
        `"${getChainName(location.chainId)}"`,
        `$${parseFloat(location.retailPrice).toFixed(3)}`,
        `$${parseFloat(location.userPrice).toFixed(3)}`,
        `$${location.savings}`,
        location.distance || 'N/A'
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    saveAs(blob, `Pricing_Quote_${carrierName.replace(/[^a-z0-9]/gi, '_')}_${timestamp}.csv`);
  };

  const exportToPDF = () => {
    if (sortedLocations.length === 0) {
      alert('No data to export');
      return;
    }

    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const timestamp = new Date().toLocaleDateString();
    
    // Helper function to add page header
    const addPageHeader = (pageNum) => {
      // Add thin border line at bottom
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.5);
      doc.line(0, 35, pageWidth, 35);
      
      // Company info on left side
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(60, 60, 60);
      doc.text('FLN Fuel Network', margin, 15);
      
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text('1018 S Washington Ave, Emmett ID 83617', margin, 22);
      doc.text('fln.fuellink.net', margin, 28);
      
      // Page number on right side (if not first page)
      if (pageNum > 1) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(`Page ${pageNum}`, pageWidth - margin - 20, 20);
      }
      
      doc.setTextColor(0, 0, 0); // Reset to black
    };

    // Helper function to add page footer
    const addPageFooter = (pageNum, totalPages) => {
      doc.setFontSize(7);
      doc.setFont('helvetica', 'italic');
      doc.text(`*Pricing generated on ${timestamp}. Prices change daily with market fluctuations.`, 
        margin, pageHeight - 25);
      doc.text('Discounts remain within advertised ranges but will fluctuate with daily pricing updates.', 
        margin, pageHeight - 18);
      doc.text('We strive to provide accurate information, but discrepancies may occur.', 
        margin, pageHeight - 11);
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text(`Page ${pageNum} of ${totalPages}`, pageWidth - margin - 25, pageHeight - 5);
    };

    // Calculate total pages needed
    const locationsPerPage = 25;
    const totalPages = Math.ceil(sortedLocations.length / locationsPerPage) + 1; // +1 for summary page

    let currentPage = 1;
    let currentY = 40;

    // Page 1: Summary Information
    addPageHeader(currentPage);
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    currentY = 50; // Start below the header
    
    // Summary section with professional styling
    doc.setDrawColor(241, 101, 40); // Orange color to match theme
    doc.setLineWidth(0.5);
    doc.line(margin, currentY - 5, pageWidth - margin, currentY - 5);
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Quote Summary', margin, currentY + 5);
    currentY += 12;
    
    // Add the main title here instead of header
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(241, 101, 40); // Orange color for emphasis
    const maxCarrierLength = 40;
    const displayCarrierName = carrierName.length > maxCarrierLength ? 
      carrierName.substring(0, maxCarrierLength - 3) + '...' : carrierName;
    doc.text(`Location Pricing Generated For: ${displayCarrierName}`, margin, currentY);
    currentY += 10;
    
    doc.setTextColor(0, 0, 0); // Reset to black
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated: ${timestamp}`, margin, currentY);
    currentY += 7;
    doc.text(`Search Location: ${address}`, margin, currentY);
    currentY += 7;
    doc.text(`Search Radius: ${radius} miles`, margin, currentY);
    currentY += 7;
    
    // Truncate discount group name if too long
    const discountGroupName = selectedDiscountGroupDetails?.name || 'N/A';
    const maxGroupNameLength = 50;
    const displayGroupName = discountGroupName.length > maxGroupNameLength ? 
      discountGroupName.substring(0, maxGroupNameLength - 3) + '...' : discountGroupName;
    doc.text(`Discount Group: ${displayGroupName}`, margin, currentY);
    currentY += 7;
    doc.text(`Total Locations Found: ${sortedLocations.length}`, margin, currentY);
    currentY += 10;

    // Add notes if present
    if (notes.trim() !== '') {
      doc.setFont('helvetica', 'bold');
      doc.text('Notes:', margin, currentY);
      currentY += 6;
      doc.setFont('helvetica', 'normal');
      
      // Split notes into multiple lines if needed
      const notesLines = doc.splitTextToSize(notes, pageWidth - 2 * margin);
      notesLines.forEach(line => {
        doc.text(line, margin, currentY);
        currentY += 6;
      });
      currentY += 6;
    }
    
    // Transaction fees section
    if (selectedDiscountGroupDetails) {
      doc.setDrawColor(241, 101, 40); // Orange color to match theme
      doc.line(margin, currentY - 5, pageWidth - margin, currentY - 5);
      
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('Transaction Fees & Details', margin, currentY + 5);
      currentY += 12;
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text(`In-Network Transaction Fee: $${parseFloat(selectedDiscountGroupDetails.transactionFee).toFixed(2)}`, margin, currentY);
      currentY += 7;
      doc.text(`Out of Network Transaction Fee: $${parseFloat(selectedDiscountGroupDetails.outOfNetworkTransactionFee).toFixed(2)}`, margin, currentY);
      currentY += 7;
      doc.text(`Cash Advance Fee: $${parseFloat(selectedDiscountGroupDetails.cashAdvanceFee).toFixed(2)}`, margin, currentY);
      currentY += 10;
      
      // Fuel types
      const fuelTypes = [];
      if (selectedDiscountGroupDetails.fuelTypes?.diesel) fuelTypes.push('Diesel');
      if (selectedDiscountGroupDetails.fuelTypes?.gas) fuelTypes.push('Gas');
      if (selectedDiscountGroupDetails.fuelTypes?.def) fuelTypes.push('DEF');
      if (selectedDiscountGroupDetails.fuelTypes?.reeferFuel) fuelTypes.push('Reefer Fuel');
      if (selectedDiscountGroupDetails.fuelTypes?.lng) fuelTypes.push('LNG');
      
      if (fuelTypes.length > 0) {
        doc.setFont('helvetica', 'bold');
        doc.text('Authorized Fuel Types:', margin, currentY);
        currentY += 8;
        doc.setFont('helvetica', 'normal');
        doc.text(fuelTypes.join(', '), margin, currentY);
        currentY += 12;
      }
    }

    addPageFooter(currentPage, totalPages);

    // Process locations in chunks for pagination
    let locationIndex = 0;
    while (locationIndex < sortedLocations.length) {
      doc.addPage();
      currentPage++;
      currentY = 30;
      
      addPageHeader(currentPage);
      
      // Table header with professional styling
      currentY = 50; // Start below the header
      doc.setDrawColor(241, 101, 40); // Orange color
      doc.setFillColor(255, 245, 238); // Light orange background
      doc.rect(margin, currentY - 3, pageWidth - 2 * margin, 10, 'FD');
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('Location', margin + 2, currentY + 4);
      doc.text('City, State', margin + 42, currentY + 4);
      doc.text('Chain', margin + 75, currentY + 4);
      doc.text('Retail', margin + 95, currentY + 4);
      doc.text('Your Price', margin + 115, currentY + 4);
      doc.text('Savings', margin + 140, currentY + 4);
      doc.text('Distance', margin + 165, currentY + 4);
      
      currentY += 12;
      doc.setFont('helvetica', 'normal');
      
      // Table data
      const endIndex = Math.min(locationIndex + locationsPerPage, sortedLocations.length);
      for (let i = locationIndex; i < endIndex; i++) {
        const location = sortedLocations[i];
        
        if (currentY > pageHeight - 30) {
          addPageFooter(currentPage, totalPages);
          doc.addPage();
          currentPage++;
          addPageHeader(currentPage);
          currentY = 60;
          
          // Re-add table header
          doc.setDrawColor(241, 101, 40); // Orange color
          doc.setFillColor(255, 245, 238); // Light orange background
          doc.rect(margin, currentY - 3, pageWidth - 2 * margin, 10, 'FD');
          
          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          doc.text('Location', margin + 2, currentY + 4);
          doc.text('City, State', margin + 42, currentY + 4);
          doc.text('Chain', margin + 75, currentY + 4);
          doc.text('Retail', margin + 95, currentY + 4);
          doc.text('Your Price', margin + 115, currentY + 4);
          doc.text('Savings', margin + 140, currentY + 4);
          doc.text('Distance', margin + 165, currentY + 4);
          
          currentY += 12;
          doc.setFont('helvetica', 'normal');
        }
        
        // Zebra striping for better readability
        if ((i - locationIndex) % 2 === 1) {
          doc.setFillColor(248, 249, 250);
          doc.rect(margin, currentY - 3, pageWidth - 2 * margin, 8, 'F');
        }
        
        // Location data with proper spacing
        const locationText = location.locationName.length > 16 ? 
          location.locationName.substring(0, 14) + '...' : location.locationName;
        const cityState = `${location.city}, ${location.state}`;
        const cityStateText = cityState.length > 14 ? cityState.substring(0, 12) + '...' : cityState;
        const chainName = getChainName(location.chainId);
        const chainText = chainName.length > 10 ? chainName.substring(0, 8) + '...' : chainName;
        
        doc.setFontSize(8);
        doc.text(locationText, margin + 2, currentY + 2);
        doc.text(cityStateText, margin + 42, currentY + 2);
        doc.text(chainText, margin + 75, currentY + 2);
        
        // Price formatting with colors
        doc.setTextColor(220, 53, 69); // Red for retail
        doc.text(`$${parseFloat(location.retailPrice).toFixed(3)}`, margin + 95, currentY + 2);
        
        doc.setTextColor(40, 167, 69); // Green for your price
        doc.text(`$${parseFloat(location.userPrice).toFixed(3)}`, margin + 115, currentY + 2);
        
        doc.setTextColor(23, 162, 184); // Blue for savings
        doc.text(`$${location.savings}`, margin + 140, currentY + 2);
        
        // Add distance column
        doc.setTextColor(0, 0, 0); // Black for distance
        doc.text(`${location.distance} mi`, margin + 165, currentY + 2);
        
        doc.setTextColor(0, 0, 0); // Reset to black
        currentY += 8;
      }
      
      locationIndex = endIndex;
      addPageFooter(currentPage, totalPages);
    }
    
    const pdfTimestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    doc.save(`Pricing_Quote_${carrierName.replace(/[^a-z0-9]/gi, '_')}_${pdfTimestamp}.pdf`);
  };

  const handleMapLoad = useCallback((map) => {
    setMapInstance(map);
  }, []);

  const renderMarkers = () => {
    return sortedLocations.map((location) => {
      if (!location.retailPrice || location.retailPrice === "NA") return null;

      const markerIcon = theme.markerIcon ? {
        url: theme.markerIcon,
        labelOrigin: new window.google.maps.Point(15, 45),
        scaledSize: new window.google.maps.Size(30, 30)
      } : {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512">
            <path fill="${theme.primary}" d="M215.7 499.2C267 435 384 279.4 384 192C384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 168.3 307.2c12.3 15.3 35.1 15.3 47.4 0zM192 128a64 64 0 1 1 0 128 64 64 0 1 1 0-128z"/>
          </svg>
        `)}`,
        labelOrigin: new window.google.maps.Point(12, 45),
        scaledSize: new window.google.maps.Size(30, 30)
      };

      return (
        <Marker
          key={`marker-${location.locationID}`}
          position={{
            lat: parseFloat(location.latitude),
            lng: parseFloat(location.longitude),
          }}
          icon={markerIcon}
          onClick={() => setSelectedLocation(location)}
          label={{
            text: `$${parseFloat(location.userPrice).toFixed(3)}`,
            color: "#000000",
            fontSize: "14px",
            fontWeight: "bold",
            className: "price-label"
          }}
        />
      );
    });
  };

  const renderInfoWindow = () => {
    if (!selectedLocation) return null;

    const price = `$${parseFloat(selectedLocation.userPrice).toFixed(3)}`;
    const retailPrice = `$${parseFloat(selectedLocation.retailPrice).toFixed(3)}`;
    const savings = `$${selectedLocation.savings}`;
    const distance = selectedLocation.distance ? `${selectedLocation.distance} miles` : "";
    const modifiedOn = new Date(selectedLocation.modifiedOn).toLocaleString();

    return (
      <InfoWindow
        position={{
          lat: parseFloat(selectedLocation.latitude),
          lng: parseFloat(selectedLocation.longitude),
        }}
        onCloseClick={() => setSelectedLocation(null)}
      >
        <div className="bg-white p-4 max-w-sm">
          <h3 className="font-semibold text-lg mb-2">{selectedLocation.locationName}</h3>
          <div className="text-green-600 font-bold text-lg">{price}</div>
          <div className="text-sm text-gray-500 mb-1">Your Price</div>
          <div className="text-red-600">{retailPrice}</div>
          <div className="text-sm text-gray-500 mb-1">Retail Price</div>
          <div className="text-blue-600 font-semibold">Savings: {savings}</div>
          <div className="mt-2">
            <div>{selectedLocation.addr1}</div>
            <div>{selectedLocation.city}, {selectedLocation.state} {selectedLocation.zip}</div>
            {distance && <div className="text-sm text-gray-600 mt-1">Distance: {distance}</div>}
          </div>
          <div className="text-xs text-gray-400 mt-2">Updated: {modifiedOn}</div>
        </div>
      </InfoWindow>
    );
  };

  const renderTableView = () => (
    <div className="overflow-x-auto">
      <table className="min-w-full bg-white border border-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left cursor-pointer hover:bg-gray-100" onClick={() => handleSort('locationName')}>
              <div className="flex items-center">
                Location Name
                <FontAwesomeIcon icon={getSortIcon('locationName')} className="ml-1 text-gray-400" />
              </div>
            </th>
            <th className="px-4 py-3 text-left cursor-pointer hover:bg-gray-100" onClick={() => handleSort('city')}>
              <div className="flex items-center">
                City/State
                <FontAwesomeIcon icon={getSortIcon('city')} className="ml-1 text-gray-400" />
              </div>
            </th>
            <th className="px-4 py-3 text-left">Chain</th>
            <th className="px-4 py-3 text-left cursor-pointer hover:bg-gray-100" onClick={() => handleSort('retailPrice')}>
              <div className="flex items-center">
                Retail Price
                <FontAwesomeIcon icon={getSortIcon('retailPrice')} className="ml-1 text-gray-400" />
              </div>
            </th>
            <th className="px-4 py-3 text-left cursor-pointer hover:bg-gray-100" onClick={() => handleSort('userPrice')}>
              <div className="flex items-center">
                Your Price
                <FontAwesomeIcon icon={getSortIcon('userPrice')} className="ml-1 text-gray-400" />
              </div>
            </th>
            <th className="px-4 py-3 text-left cursor-pointer hover:bg-gray-100" onClick={() => handleSort('savings')}>
              <div className="flex items-center">
                Savings
                <FontAwesomeIcon icon={getSortIcon('savings')} className="ml-1 text-gray-400" />
              </div>
            </th>
            <th className="px-4 py-3 text-left cursor-pointer hover:bg-gray-100" onClick={() => handleSort('distance')}>
              <div className="flex items-center">
                Distance
                <FontAwesomeIcon icon={getSortIcon('distance')} className="ml-1 text-gray-400" />
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedLocations.map((location, index) => (
            <tr key={location.locationID} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className="px-4 py-3 font-medium">{location.locationName}</td>
              <td className="px-4 py-3">{location.city}, {location.state}</td>
              <td className="px-4 py-3">{getChainName(location.chainId)}</td>
              <td className="px-4 py-3 text-red-600">${parseFloat(location.retailPrice).toFixed(3)}</td>
              <td className="px-4 py-3 text-green-600 font-semibold">${parseFloat(location.userPrice).toFixed(3)}</td>
              <td className="px-4 py-3 text-blue-600 font-semibold">${location.savings}</td>
              <td className="px-4 py-3">{location.distance ? `${location.distance} mi` : 'N/A'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderMapView = () => (
    <GoogleMap
      onLoad={handleMapLoad}
      center={mapCenter}
      zoom={mapZoom}
      mapContainerStyle={{ width: "100%", height: "500px" }}
      options={{ styles: defaultMapStyles }}
    >
      {renderMarkers()}
      {renderInfoWindow()}
    </GoogleMap>
  );

  const renderEmptyState = () => (
    <div className="bg-white rounded-lg shadow-md p-12 text-center">
      <div className="max-w-md mx-auto">
        <div className="mb-6">
          <FontAwesomeIcon 
            icon={faClipboardList} 
            className="text-6xl text-gray-300 mb-4" 
          />
        </div>
        <h3 className="text-xl font-semibold text-gray-800 mb-3">
          Ready to Generate Your Pricing Quote
        </h3>
        <p className="text-gray-600 mb-6 leading-relaxed">
          Fill in the carrier name, select a discount group, enter a search location and radius, then click "Search Locations" to generate a comprehensive pricing quote for your carrier.
        </p>
        <div className="flex items-center justify-center text-sm text-gray-500">
          <FontAwesomeIcon icon={faSearch} className="mr-2" />
          Enter your search criteria above to get started
        </div>
      </div>
    </div>
  );

  if (!currentUser?.isAdmin) {
    return null;
  }

  return (
    <>
      <MobileHeader theme={theme} />
      <LoadScriptNext
        googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
        libraries={googleMapsLibraries}
      >
        <div className="mobile-container p-6">
        <PageHeader
          heading="Carrier Pricing Quotes"
          context="Generate branded pricing quotes for carriers using specific discount groups."
        />
        
        {loading && <Spinner />}
        
        {/* Search Form */}
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Carrier Name</label>
              <input
                type="text"
                value={carrierName}
                onChange={(e) => setCarrierName(e.target.value)}
                placeholder="Enter carrier name for branding"
                className="w-full p-3 border-2 border-gray-300 rounded focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Discount Group</label>
              <select
                value={selectedDiscountGroup}
                onChange={(e) => setSelectedDiscountGroup(e.target.value)}
                className="w-full p-3 border-2 border-gray-300 rounded focus:border-blue-500"
                required
              >
                <option value="">Select Discount Group</option>
                {discountGroups.map((group) => (
                  <option key={group.ID} value={group.ID}>
                    {group.DiscountGroupName}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Search Location</label>
              <PlacesAutocomplete
                value={address}
                onChange={setAddress}
                onSelect={handleSelect}
                searchOptions={{
                  types: ['(cities)'],
                  componentRestrictions: { country: ['us', 'ca'] }
                }}
              >
                {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                  <div className="relative">
                    <input
                      {...getInputProps({ placeholder: "Enter city, state" })}
                      className="w-full p-3 border-2 border-gray-300 rounded focus:border-blue-500"
                      required
                    />
                    {(loading || suggestions.length > 0) && (
                      <div className="absolute inset-x-0 top-full bg-white border-2 border-t-0 border-gray-300 z-10 max-h-48 overflow-y-auto">
                        {loading && <div className="p-2">Loading...</div>}
                        {suggestions.map((suggestion) => (
                          <div
                            {...getSuggestionItemProps(suggestion)}
                            key={suggestion.placeId}
                            className="p-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100"
                          >
                            {suggestion.description}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </PlacesAutocomplete>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Radius (miles)</label>
              <input
                type="number"
                max="3500"
                value={radius}
                onChange={(e) => setRadius(Math.min(3500, e.target.value))}
                placeholder="Search radius"
                className="w-full p-3 border-2 border-gray-300 rounded focus:border-blue-500"
                required
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any additional notes for this pricing quote..."
              className="w-full p-3 border-2 border-gray-300 rounded focus:border-blue-500"
              rows={3}
            />
          </div>
          
          <button
            onClick={fetchFuelStops}
            className="themed-button px-6 py-3 rounded font-semibold"
            disabled={!isFormValid}
          >
            Search Locations
          </button>
        </div>

        {/* Empty State - Show when no search has been performed */}
        {!hasSearched && !loading && renderEmptyState()}

        {/* Results Section */}
        {hasSearched && locations.length > 0 && (
          <div className="bg-white rounded-lg shadow-md">
            {/* View Controls */}
            <div className="p-4 border-b border-gray-200 flex flex-wrap items-center justify-between">
              <div className="flex space-x-2 mb-2 md:mb-0">
                <button
                  onClick={() => setViewMode('table')}
                  className={`px-4 py-2 rounded flex items-center ${viewMode === 'table' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                >
                  <FontAwesomeIcon icon={faTable} className="mr-2" />
                  Table
                </button>
                <button
                  onClick={() => setViewMode('map')}
                  className={`px-4 py-2 rounded flex items-center ${viewMode === 'map' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                >
                  <FontAwesomeIcon icon={faMap} className="mr-2" />
                  Map
                </button>
              </div>
              
              <div className="flex space-x-2">
                <button
                  onClick={exportToCSV}
                  className="themed-button px-4 py-2 rounded flex items-center"
                >
                  <FontAwesomeIcon icon={faFileCsv} className="mr-2" />
                  Export CSV
                </button>
                <button
                  onClick={exportToPDF}
                  className="themed-button px-4 py-2 rounded flex items-center"
                >
                  <FontAwesomeIcon icon={faFilePdf} className="mr-2" />
                  Export PDF
                </button>
              </div>
            </div>

            {/* Results Count */}
            <div className="px-4 py-2 bg-gray-50 text-sm text-gray-600">
              Found {locations.length} locations for {carrierName} 
              {selectedDiscountGroup && discountGroups.find(dg => dg.ID == selectedDiscountGroup) && 
                ` using ${discountGroups.find(dg => dg.ID == selectedDiscountGroup).DiscountGroupName} pricing`
              }
            </div>

            {/* Content */}
            <div className="p-4">
              {viewMode === 'table' && renderTableView()}
              {viewMode === 'map' && renderMapView()}
            </div>
          </div>
        )}

        {/* No Results Message - Show when search was performed but no results */}
        {hasSearched && locations.length === 0 && !loading && (
          <div className="bg-white p-8 rounded-lg shadow-md text-center">
            <div className="max-w-md mx-auto">
              <FontAwesomeIcon icon={faMapMarkerAlt} className="text-4xl text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">No Locations Found</h3>
              <p className="text-gray-500">No locations found for the specified criteria. Try expanding your search radius or choosing a different location.</p>
            </div>
          </div>
        )}
        </div>
      </LoadScriptNext>
    </>
  );
};

export default AdminLocationFinder;